// Cookie-based identity for the widget. Two parts live in one cookie:
//
//   - creatorToken: opaque UUID, minted on first use. Anchors authorship until
//     the magic-link binding upgrades it to a reviewer profile. Persists for
//     365 days so reload + tab-switch keep ownership of in-flight comments.
//
//   - reviewer: cached profile fetched from the server after a successful
//     auth-handoff exchange (URL JWT or iframe handshake). Null until the
//     reviewer signs in. The presence of `reviewer` is the gate the UI uses
//     to decide whether to show the login wall or the real composer.
//
// Anonymous "type your name only" mode is gone — comments require a confirmed
// reviewer binding server-side, so the widget enforces the same up front.

const COOKIE = 'pinmerge_id';
const MAX_AGE = 60 * 60 * 24 * 365;

export interface ReviewerCache {
  id: string;
  email: string;
  name: string | null;
  avatar_id: string | null;
  avatar_bg: string | null;
}

export interface Identity {
  creatorToken: string;
  reviewer: ReviewerCache | null;
}

interface StoredIdentity {
  creatorToken?: string;
  reviewer?: ReviewerCache | null;
}

function readCookie(): StoredIdentity | null {
  const raw = document.cookie
    .split('; ')
    .find((c) => c.startsWith(`${COOKIE}=`))
    ?.slice(COOKIE.length + 1);
  if (!raw) return null;
  try {
    return JSON.parse(decodeURIComponent(raw)) as StoredIdentity;
  } catch {
    return null;
  }
}

function writeCookie(value: StoredIdentity): void {
  const encoded = encodeURIComponent(JSON.stringify(value));
  document.cookie = `${COOKIE}=${encoded}; max-age=${MAX_AGE}; path=/; SameSite=Lax`;
}

// Returns the current creatorToken + cached reviewer (which may be null).
// Always mints a creatorToken if missing — every widget interaction needs one
// so the server has an anchor when the binding eventually lands.
export function getIdentity(): Identity {
  const stored = readCookie() ?? {};
  let creatorToken = stored.creatorToken;
  if (!creatorToken) {
    creatorToken = crypto.randomUUID();
    writeCookie({ ...stored, creatorToken });
  }
  return {
    creatorToken,
    reviewer: stored.reviewer ?? null,
  };
}

export function setReviewer(reviewer: ReviewerCache): void {
  const stored = readCookie() ?? {};
  writeCookie({ ...stored, reviewer });
}

export function clearReviewer(): void {
  const stored = readCookie() ?? {};
  delete stored.reviewer;
  writeCookie(stored);
}

// Best display name for a reviewer: explicit name first, else the local part
// of their email, else 'Reviewer' as a hard fallback. Used everywhere the UI
// renders an author name + initial.
export function displayNameFor(reviewer: ReviewerCache): string {
  if (reviewer.name && reviewer.name.trim()) return reviewer.name.trim();
  const local = reviewer.email.split('@')[0];
  return local && local.length > 0 ? local : 'Reviewer';
}

